import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import './style.css';
import './theme-tokens.generated.css';
import App from './App';
import { getAuthToken } from './utils/auth.js';
import { ThemeProvider } from './contexts/ThemeContext.jsx';

// @probestack/probestack-ui-library ships pre-compiled Tailwind v4 CSS. This
// app runs Tailwind v3, whose PostCSS pipeline doesn't understand v4-only
// syntax (`@layer properties`, `@property`) and would corrupt the library's
// styles if the CSS were imported normally. Pulling it in as a raw string
// (via the `?raw` loader rule in craco.config.js) and injecting it directly
// bypasses that pipeline entirely — same approach forgegateway's main.jsx
// uses. The @property strip below covers browsers/build steps that choke on
// bare @property blocks outside of @layer.
// Resolves via the 'probestack-ui-library-style-css' webpack alias in
// craco.config.js — see that file for why a plain package-specifier import
// with "?raw" doesn't work here.
import libraryStyles from 'probestack-ui-library-style-css?raw';

const strippedLibraryStyles = libraryStyles.replace(/@property\s+--[\w-]+\s*\{[^{}]*\}/g, '');
const probestackLibraryStyleTag = document.createElement('style');
probestackLibraryStyleTag.innerHTML = strippedLibraryStyles;
document.head.appendChild(probestackLibraryStyleTag);

// --- Light-theme cascade fix for @probestack/probestack-ui-library ---
// The shipped bundle keys its dark palette off the bare `.probestack-ui-library`
// class rather than `[data-theme="dark"]` (`.probestack-ui-library,[data-theme=dark]{...}`),
// so any element the library renders with that class but without its own
// `data-theme` attribute (e.g. nested wrapper/portal divs inside the header's
// ProfileDropdown menu) resets its CSS variables back to the dark palette even
// while sitting inside a `data-theme="light"` popup. Because the popup's own
// background correctly resolves to light (white), those inner resets produce
// near-white text/icons on a white background — the "blank" light-mode popup
// reported. The bundle's own `[data-theme="light"]{...}` rule (which sets the
// correct light values) loses to that dark rule in some nested cases because
// both share equal specificity and DOM order varies by portal. This block
// restates the real light-theme tokens with strictly higher specificity so
// they always win for any subtree that is actually inside a light-mode
// container, without touching the library's dark-mode styling at all.
const themeCascadeFixStyleTag = document.createElement('style');
themeCascadeFixStyleTag.innerHTML = `
[data-theme="light"] .probestack-ui-library:not([data-theme="dark"]),
[data-theme="light"].probestack-ui-library:not([data-theme="dark"]) {
  --color-probestack-bg:#f6f7fb;
  --color-surface:#fff;
  --color-elevated:#fff;
  --color-hover:#f1f3f9;
  --color-border:#e5e7eb;
  --color-border-subtle:#f1f3f9;
  --color-primary:#ff5b1f;
  --color-primary-hover:#e04f17;
  --color-primary-light:#ff8c4a;
  --color-primary-muted:#ff5b1f14;
  --color-text-primary:#1f2937;
  --color-text-secondary:#6b7280;
  --color-text-muted:#9ca3af;
  --color-text-inverse:#fff;
  --color-success:#16a34a;
  --color-success-muted:#16a34a1a;
  --color-warning:#d97706;
  --color-warning-muted:#d977061a;
  --color-danger:#dc2626;
  --color-danger-muted:#dc26261a;
  --color-info:#2563eb;
  --color-info-muted:#2563eb1a;
  --color-method-get:#16a34a;
  --color-method-post:#d97706;
  --color-method-put:#2563eb;
  --color-method-patch:#9333ea;
  --color-method-delete:#dc2626;
  --color-method-head:#475569;
  --color-method-options:#475569;
  --color-input-bg:#fff;
  --color-input-border:#e5e7eb;
  --color-input-focus:#ff5b1f;
  --shadow-sm:0 1px 2px #1018280d;
  --shadow-md:0 4px 12px #10182814;
  --shadow-lg:0 12px 32px #1018281f;
  --shadow-glow:0 0 0 1px #ff5b1f4d, 0 4px 24px #ff5b1f33;
  color-scheme: light;
}

[data-theme="light"] {
  color-scheme: light;
}

/* This app's own global CSS also sets an unlayered, app-wide default text
   color (e.g. src/index.css's \`body { color: #fff }\`) which - being
   unlayered - outranks any of the library's own (real-@layer, Tailwind v4)
   utility classes for any element that doesn't itself carry a more specific
   unlayered color. Re-assert the correct light-theme text color directly,
   unlayered, scoped to the light-mode popup/menu root so it wins over that
   app-wide default without touching elements that already set their own
   accent color (danger/success/primary/etc. classes still apply normally). */
[data-theme="light"] .probestack-ui-library:not([data-theme="dark"]),
[data-theme="light"].probestack-ui-library:not([data-theme="dark"]) {
  color: var(--color-text-primary);
}

/* Smooth, premium-feeling transition when the toggle flips themes, instead
   of an abrupt flash between palettes. */
.probestack-ui-library,
.probestack-ui-library * {
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease;
}
`;
document.head.appendChild(themeCascadeFixStyleTag);

// The ForgeSphere landing page forwards the active session as URL query
// params when linking here (separate origin, so localStorage isn't shared).
// Mirrors hydrateAuthContextFromUrl in forgegateway's main.jsx.
const hydrateAuthContextFromUrl = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const userEmail = params.get('userEmail')?.trim();
  const authToken = params.get('authToken')?.trim();
  const userRole = params.get('userRole')?.trim();
  const organizationId = params.get('organizationId')?.trim();

  // A different token means a different session (possibly a different
  // user). Drop the previous session's identity keys so the header never
  // shows a stale name/email while useProbeStackProfile resolves the real
  // user from the new token.
  const incomingToken = authToken || getAuthToken();
  if (incomingToken && incomingToken !== localStorage.getItem('authToken')) {
    ['userEmail', 'userRole', 'userFirstName', 'userName', 'userOrganization', 'userId', 'userOrganizationId', 'forgesphere.session.profile'].forEach((key) =>
      localStorage.removeItem(key),
    );
  }

  if (userEmail) localStorage.setItem('userEmail', userEmail);
  if (authToken) localStorage.setItem('authToken', authToken);
  if (userRole) localStorage.setItem('userRole', userRole);
  if (organizationId) {
    localStorage.setItem('organizationId', organizationId);
    localStorage.setItem('userOrganizationId', organizationId);
  }

  // Direct SSO entry provides the session through the shared cookie. Mirror
  // it into localStorage so a stale token from a previous session isn't
  // reused. Do not overwrite a token supplied in the URL.
  if (!authToken) {
    const cookieToken = getAuthToken();
    if (cookieToken) localStorage.setItem('authToken', cookieToken);
  }

  const authParamNames = ['userEmail', 'authToken', 'userRole', 'organizationId'];
  const hasAuthParams = authParamNames.some((name) => params.has(name));
  if (!hasAuthParams) return;

  authParamNames.forEach((name) => params.delete(name));
  const remainingQuery = params.toString();
  window.history.replaceState(
    window.history.state,
    document.title,
    `${window.location.pathname}${remainingQuery ? `?${remainingQuery}` : ''}${window.location.hash}`,
  );
};

hydrateAuthContextFromUrl();

// Import Syne font for header text matching ProbeStack brand
const link = document.createElement('link');
link.href = 'https://fonts.googleapis.com/css2?family=Syne:wght=700;800&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap';
link.rel = 'stylesheet';
document.head.appendChild(link);

// Global reset
//
// This is wrapped in an explicit `app-reset` layer, declared lower-priority
// than the @probestack/probestack-ui-library's own `theme`/`base`/`utilities`
// layers (Tailwind v4 output). Without this, `body { color: #F8FAFF }` below
// is an *unlayered* rule, and per the CSS Cascade Layers spec an unlayered
// declaration always wins over a layered one regardless of specificity. That
// silently defeated the library's own `.text-text-primary{color:var(--color-
// text-primary)}` utility (itself inside `@layer utilities`) everywhere the
// library renders text (e.g. the header's ProfileDropdown menu) - text
// stayed pinned to this near-white reset color even after switching the
// theme toggle to light mode, while backgrounds (which don't inherit)
// switched correctly - producing invisible white-on-white popup text.
// Declaring the layer order here retroactively slots `app-reset` beneath the
// library's layers so its utility classes can win as intended, while this
// reset still applies normally as a baseline anywhere the library isn't
// rendering.
const style = document.createElement('style');
style.textContent = `
  @layer app-reset, theme, base, components, utilities;
  @layer app-reset {
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { background: var(--background, #060E1C); color: var(--foreground, #F8FAFF); -webkit-font-smoothing: antialiased; }
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: var(--background, #0A1628); }
    ::-webkit-scrollbar-thumb { background: var(--background-elevated, #1E3A5A); border-radius: 3px; }
    input, select, button { font-family: inherit; }
  }
`;
document.head.appendChild(style);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ThemeProvider>
  </React.StrictMode>
);
